/**
 * Upcoming Match Planning — shared policy for all "fill upcoming slot" paths.
 *
 * Three pure functions (no setState, no Supabase) that encode the single canonical
 * rules for player eligibility, session state construction, and matchmaking/scheduled-
 * game selection.  All callers (generateMissingUpcomingCourts, handleFillUpcomingCourt,
 * fillPlayingCourt) delegate to these helpers so divergences between auto and manual
 * paths are eliminated.
 *
 * Callers remain responsible for:
 *   - Reading the snapshot from React state / syncExtras
 *   - Applying the result via setState / OCC writes
 *   - Surface-level differences (partnership gate, user-facing errors, slot guards)
 */

import {
  coalescePlayerInteractionsForMatchmaking,
  deriveConsistentWaitingList,
} from './sessionMatchModel.js';
import { isSinglesFormat } from './competitiveMatchFormat.js';
import { calculateSessionMinGames } from './poolBuilder.js';
import { filterMatchmakingEligiblePlayerIds } from './matchmakingEligibility.js';

// ---------------------------------------------------------------------------
// Shared eligibility helpers (exported for server path + tests)
// ---------------------------------------------------------------------------

/**
 * Authoritative cooldown check that unions gameplay policy (`playerWaitMetrics`) and
 * the transition-correctness flag materialised on frozen player rows (`playersById`).
 * Either source may be stale independently; checking both is belt-and-suspenders.
 */
export function resolvePlayerOnCooldown(playerId, { playerWaitMetrics, playersById }) {
  return (
    playerWaitMetrics?.[playerId]?.isOnCooldown === true ||
    playersById?.[playerId]?.isOnCooldown === true
  );
}

/**
 * Builds the `playerCategories` map expected by the two-stage engine from the
 * frozen `players_by_id` object (populated by migration 099 on the server path,
 * or by `buildPlayerCategories` on the client path).
 * Returns `null` when no player has category data, so callers can treat it as
 * "categories disabled" the same way the legacy path does.
 */
export function buildPlayerCategoriesFromPlayersById(playersById) {
  const categories = {};
  for (const [id, player] of Object.entries(playersById || {})) {
    if (player?.mixed_category || player?.levels_category) {
      categories[id] = {
        mixed: player.mixed_category,
        levels: player.levels_category,
        calculatedAt: player.category_calculated_at ?? null,
      };
    }
  }
  return Object.keys(categories).length > 0 ? categories : null;
}

import { GAME_CREATION_ORIGIN, MATCHMAKING_ALGORITHM_NAME } from './gameCreation.js';

/** True when a scheduled_games row has a complete singles or doubles roster. */
export function isScheduledGameRosterComplete(game, matchFormat) {
  if (!game) return false;
  const team1 = [game.team1_player1_id, game.team1_player2_id].filter(Boolean);
  const team2 = [game.team2_player1_id, game.team2_player2_id].filter(Boolean);
  if (isSinglesFormat(matchFormat)) {
    return team1.length === 1 && team2.length === 1;
  }
  return team1.length === 2 && team2.length === 2;
}

/** True when at least one scheduled game in the pool can be promoted as a full unit. */
export function hasPromotableScheduledGame(validScheduledGames, matchFormat) {
  return (validScheduledGames || []).some((game) =>
    isScheduledGameRosterComplete(game, matchFormat),
  );
}

function scheduledGamePlayerIds(game) {
  return [
    game?.team1_player1_id,
    game?.team1_player2_id,
    game?.team2_player1_id,
    game?.team2_player2_id,
  ].filter(Boolean);
}

/** Prefer roster_player_ids; fall back to team FK columns. */
export function rosterPlayerIdsFromScheduledGame(game) {
  if (Array.isArray(game?.roster_player_ids) && game.roster_player_ids.length > 0) {
    return game.roster_player_ids;
  }
  return scheduledGamePlayerIds(game);
}

/**
 * Players reserved by non-completed scheduled games — used for waitlist badges
 * and matchmaking exclusion until the scheduled game completes.
 */
export function buildSessionPlayerScheduledGamesMap(scheduledGames) {
  const map = {};
  for (const game of scheduledGames || []) {
    if (game?.status === 'completed') continue;
    for (const playerId of rosterPlayerIdsFromScheduledGame(game)) {
      map[playerId] = game.id;
    }
  }
  return map;
}

function buildReservedScheduledPlayerIds(scheduledGames, sessionPlayerScheduledGames, matchFormat) {
  const reservedScheduledPlayerIds = new Set();
  const validScheduledGames = [];

  for (const game of scheduledGames || []) {
    if (game?.status === 'completed') continue;
    const roster = rosterPlayerIdsFromScheduledGame(game);
    for (const id of roster) reservedScheduledPlayerIds.add(id);
    if (isScheduledGameRosterComplete(game, matchFormat)) {
      validScheduledGames.push(game);
    }
  }

  for (const [playerId, gameId] of Object.entries(sessionPlayerScheduledGames || {})) {
    if (gameId) reservedScheduledPlayerIds.add(playerId);
  }

  return { reservedScheduledPlayerIds, validScheduledGames };
}

/**
 * When building the non-cooldown tier, drop a player whose partner is still in the
 * full pool but on cooldown — prevents lone-partner matchmaking in tier 1.
 */
function excludePartnersWithCooldownPartner(poolIds, { finalPoolSet, onCooldown, partnerships }) {
  if (!partnerships?.length) return poolIds;

  const partnerOf = new Map();
  for (const p of partnerships) {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    if (p1 && p2) {
      partnerOf.set(p1, p2);
      partnerOf.set(p2, p1);
    }
  }

  return poolIds.filter((id) => {
    const partnerId = partnerOf.get(id);
    if (!partnerId || !finalPoolSet.has(partnerId)) return true;
    return !onCooldown(partnerId);
  });
}

/**
 * Exclude partnered players whose partner is not in the same candidate pool
 * (e.g. partner on court while this player is on the waitlist).
 * Mirrors twoStageEngine.filterPlayersWithAvailablePartners.
 */
export function filterPlayersWithAvailablePartnersInPool(playerIds, partnerships = []) {
  if (!partnerships?.length || !playerIds?.length) return playerIds || [];

  const partnershipMap = new Map();
  for (const p of partnerships) {
    const p1 = p.player1Id || p.player1_id;
    const p2 = p.player2Id || p.player2_id;
    if (p1 && p2) {
      partnershipMap.set(p1, p2);
      partnershipMap.set(p2, p1);
    }
  }

  const poolSet = new Set(playerIds);
  return playerIds.filter((playerId) => {
    const partnerId = partnershipMap.get(playerId);
    if (!partnerId) return true;
    return poolSet.has(partnerId);
  });
}

// ---------------------------------------------------------------------------
// 1. buildUpcomingEligiblePool
// ---------------------------------------------------------------------------

/**
 * Build the canonical player pool for filling an upcoming slot.
 *
 * Combines the union of rules from both the auto (generateMissingUpcomingCourts)
 * and manual (handleFillUpcomingCourt / runMatchmakingForUpcomingCourt) paths:
 *   1. Remove already-placed players via deriveConsistentWaitingList
 *   2. Keep only `active` players
 *   3. Remove players with a scheduled_game_id
 *   4. Identify valid scheduled games (all players available) + reserve their IDs
 *   5. Remove reserved scheduled IDs from the pool
 *   6. Split into nonCooldown vs full-pool cooldown tiers
 *   7. Optionally apply partnership eligibility gate (manual path only)
 *
 * @param {object} snapshot
 * @param {string[]}  snapshot.waitingList
 * @param {object[]}  snapshot.courtAssignments
 * @param {object[]}  snapshot.nextAssignments
 * @param {object}    snapshot.playersById
 * @param {object}    snapshot.sessionPlayerStatus
 * @param {object[]}  snapshot.scheduledGames
 * @param {object}    snapshot.sessionPlayerScheduledGames   - { playerId: scheduledGameId }
 * @param {object}    snapshot.playerWaitMetrics             - { playerId: { isOnCooldown } }
 * @param {number}    snapshot.playersPerMatch
 * @param {string}    snapshot.matchFormat
 * @param {string[]}  [snapshot.hardExcludeIds] - permanent first-class exclusion layer.
 *                    These players are IMPOSSIBLE to select (finishers, injured, reserved, etc.)
 *                    and are removed before pool generation. They NEVER re-enter via cooldown
 *                    fallback widening. Separate from `isOnCooldown` (gameplay discouragement).
 * @param {Function}  [snapshot.evaluatePartnershipEligibility] - when provided, apply
 *                    partnership gate and surface errors (manual path).
 *                    Signature: (playerIds) => { eligibleIds, lockedMap }
 * @param {object[]}  [snapshot.partnerships] - active partnerships; used to keep
 *                    non-cooldown tier in sync (exclude a player when partner is on cooldown).
 *
 * @returns {{
 *   eligibleIds: string[],
 *   fullPoolIds: string[],
 *   usingFallbackPool: boolean,
 *   validScheduledGames: object[],
 *   reservedScheduledPlayerIds: Set<string>,
 *   partnershipError: string|null,
 * }}
 */
export function buildUpcomingEligiblePool({
  waitingList,
  courtAssignments,
  nextAssignments,
  playersById,
  sessionPlayerStatus,
  scheduledGames,
  sessionPlayerScheduledGames = {},
  playerWaitMetrics = {},
  playersPerMatch,
  matchFormat,
  hardExcludeIds = [],
  evaluatePartnershipEligibility = null,
  partnerships = [],
  cooldownEnabled = false,
}) {
  // Step 0 (hard exclusion): remove impossible players before any other logic.
  const hardExcludeSet = new Set(hardExcludeIds);

  // Step 1: remove already-placed players
  const { waitingList: sanitized } = deriveConsistentWaitingList(
    (waitingList ?? []).filter((id) => !hardExcludeSet.has(id)),
    courtAssignments,
    nextAssignments,
  );

  // Step 2: active only
  const activeIds = sanitized.filter((id) => {
    const player = playersById[id];
    return player && sessionPlayerStatus[id] === 'active';
  });

  // Step 3: reserve players in pending/placed scheduled games (until completed).
  const { reservedScheduledPlayerIds, validScheduledGames } = buildReservedScheduledPlayerIds(
    scheduledGames,
    sessionPlayerScheduledGames,
    matchFormat,
  );
  let finalPool = activeIds.filter((id) => !reservedScheduledPlayerIds.has(id));

  // Step 4: optional partnership gate (manual path — surfaces user-facing errors).
  let partnershipError = null;
  if (typeof evaluatePartnershipEligibility === 'function' && !isSinglesFormat(matchFormat)) {
    const { eligibleIds, lockedMap } = evaluatePartnershipEligibility(finalPool);
    if (eligibleIds.length < playersPerMatch) {
      if (lockedMap && lockedMap.size > 0) {
        const [, info] = lockedMap.entries().next().value;
        partnershipError = info?.message || 'Not enough eligible partnership players.';
      } else {
        partnershipError = 'Not enough eligible partnership players to fill this court.';
      }
    }
    finalPool = eligibleIds;
  }

  const fullPoolIds = finalPool;

  // Step 5: cooldown tiers (skipped when host disables cooldown enforcement)
  if (cooldownEnabled === false) {
    return {
      eligibleIds: fullPoolIds,
      fullPoolIds,
      usingFallbackPool: false,
      validScheduledGames,
      reservedScheduledPlayerIds,
      partnershipError,
    };
  }

  // use resolvePlayerOnCooldown so both playerWaitMetrics (gameplay policy)
  // and playersById.isOnCooldown (transition correctness) are honoured.
  const onCooldown = (id) =>
    resolvePlayerOnCooldown(id, { playerWaitMetrics, playersById });
  let nonCooldownIds = finalPool.filter((id) => !onCooldown(id));
  if (!isSinglesFormat(matchFormat) && partnerships.length > 0) {
    nonCooldownIds = excludePartnersWithCooldownPartner(nonCooldownIds, {
      finalPoolSet: new Set(finalPool),
      onCooldown,
      partnerships,
    });
  }
  const applyPartnerPoolFilter =
    !isSinglesFormat(matchFormat) && partnerships.length > 0
      ? (ids) => filterPlayersWithAvailablePartnersInPool(ids, partnerships)
      : (ids) => ids;
  const playableNonCooldown = applyPartnerPoolFilter(nonCooldownIds);
  const usingFallbackPool =
    nonCooldownIds.length < playersPerMatch
    || playableNonCooldown.length < playersPerMatch;
  const eligibleIds = usingFallbackPool ? fullPoolIds : nonCooldownIds;

  return {
    eligibleIds,
    fullPoolIds,
    usingFallbackPool,
    validScheduledGames,
    reservedScheduledPlayerIds,
    partnershipError,
  };
}

// ---------------------------------------------------------------------------
// 2. buildUpcomingSessionState
// ---------------------------------------------------------------------------

/**
 * Build the canonical sessionState object for the matchmaking engine.
 *
 * Uses the richest shape (from the manual upcoming path) so auto and manual
 * paths produce equivalent matchmaking inputs.
 *
 * @param {object} snapshot
 * @param {object}   snapshot.playersById
 * @param {string[]} snapshot.poolPlayerIds     - players whose gamesPlayedThisSession to populate
 * @param {object[]} snapshot.courtAssignments
 * @param {object[]} [snapshot.gameHistory]
 * @param {object}   [snapshot.playerInteractions]
 * @param {number}   [snapshot.totalGamesCompleted]
 * @param {object}   [snapshot.playerWaitMetrics]
 * @param {object[]} [snapshot.matchHistoryOverride]  - from syncExtras; takes priority over gameHistory
 * @param {object}   [snapshot.pendingFinishGame]     - court just scored (post-score refill)
 * @param {string[]} [snapshot.excludedPlayerIds]     - finishers merged into the waitlist pool
 *
 * @returns {object} sessionState ready to pass to getBestCourtAssignments_TwoStage
 */
export function buildUpcomingSessionState({
  playersById,
  poolPlayerIds,
  courtAssignments,
  gameHistory,
  playerInteractions,
  totalGamesCompleted,
  playerWaitMetrics,
  matchHistoryOverride,
  matchPreferencesEnabled,
  cooldownEnabled,
  partnerships = [],
  scheduledGames = [],
  relaxationConfig = null,
  sessionPlayerStatus = null,
  pendingFinishGame = null,
  excludedPlayerIds = [],
}) {
  const gamesPlayedThisSession = {};
  const rawIds =
    poolPlayerIds?.length
      ? poolPlayerIds
      : playersById && typeof playersById === 'object'
        ? Object.keys(playersById)
        : [];
  const idsForGames = sessionPlayerStatus
    ? filterMatchmakingEligiblePlayerIds(rawIds, sessionPlayerStatus)
    : rawIds;
  const sessionWideMinGames =
    idsForGames.length > 0
      ? calculateSessionMinGames(idsForGames, playersById, partnerships, scheduledGames)
      : null;
  idsForGames.forEach((id) => {
    const player = playersById?.[id];
    if (player) {
      gamesPlayedThisSession[id] =
        player.effectiveGamesPlayed !== undefined
          ? player.effectiveGamesPlayed
          : player.gamesPlayedThisSession || 0;
    }
  });

  const resolvedHistory = Array.isArray(matchHistoryOverride)
    ? matchHistoryOverride
    : Array.isArray(gameHistory)
    ? gameHistory
    : [];

  return {
    gamesPlayedThisSession,
    recentTeams: new Set(),
    recentMatchups: new Set(),
    currentlyOnCourt: new Set(
      (courtAssignments || []).flatMap((c) => [...(c.team1 || []), ...(c.team2 || [])]),
    ),
    matchHistory: resolvedHistory,
    playerInteractions: coalescePlayerInteractionsForMatchmaking(
      playerInteractions,
      resolvedHistory,
    ),
    playerWaitMetrics: playerWaitMetrics || {},
    totalGamesCompleted: totalGamesCompleted ?? resolvedHistory.length ?? 0,
    matchPreferencesEnabled: matchPreferencesEnabled !== false,
    cooldownEnabled: cooldownEnabled === true,
    sessionWideMinGames,
    sessionPlayerStatus: sessionPlayerStatus || {},
    relaxationConfig,
    pendingFinishGame: pendingFinishGame || null,
    pending_finish_game: pendingFinishGame || null,
    excludedPlayerIds: Array.isArray(excludedPlayerIds) ? excludedPlayerIds : [],
    excluded_player_ids: Array.isArray(excludedPlayerIds) ? excludedPlayerIds : [],
  };
}

// ---------------------------------------------------------------------------
// 3. planUpcomingSlot
// ---------------------------------------------------------------------------

/**
 * Determine the best upcoming slot assignment via matchmaking.
 *
 * Scheduled game placement is handled exclusively by planScheduledPlacements
 * (Phase 1 in runMatchmakingFromFrozenInput). This function is matchmaking-only.
 *
 * Pool expansion gate (inside twoStageEngine via fullWaitlistPlayerIds):
 *   Phase 1: eligibleIds (non-cooldown) at strict only
 *   Phase 2: fullPoolIds with cooldown expanded — relaxation levels before consecutive3
 *   Phase 3: fullPoolIds — consecutive3+ only as last resort
 *
 * Session game-type restrictions (e.g. balanced_only) are never silently relaxed here;
 * use the relaxation orchestrator inside twoStageEngine when the pool is exhausted.
 *
 * @param {object} pool          - output of buildUpcomingEligiblePool
 * @param {object} sessionState  - output of buildUpcomingSessionState
 * @param {object} options
 * @param {object}   options.playersById
 * @param {object[]} options.partnerships
 * @param {object}   options.playerWaitMetrics
 * @param {string}   options.gameTypeRestriction
 * @param {object|null} options.playerCategories
 * @param {string}   options.matchFormat
 * @param {number}   options.playersPerMatch
 * @param {Function} options.callMatchmakingFn
 *
 * @returns {{
 *   type: 'matchmaking'|null,
 *   teams: { team1: string[], team2: string[] } | null,
 *   gameCreation: object | null,
 *   rawCourt: object | null,
 *   selectedScheduledGame: null,
 * }}
 */
export function planUpcomingSlot(
  pool,
  sessionState,
  {
    playersById,
    partnerships,
    playerWaitMetrics,
    gameTypeRestriction,
    playerCategories,
    matchFormat,
    playersPerMatch,
    callMatchmakingFn,
  },
) {
  const { eligibleIds, fullPoolIds, usingFallbackPool } = pool;

  let rawCourt = null;
  if (eligibleIds.length >= playersPerMatch) {
    rawCourt = _runWithFallbacks(
      eligibleIds,
      fullPoolIds,
      usingFallbackPool,
      sessionState,
      playersById,
      partnerships,
      playerWaitMetrics,
      gameTypeRestriction,
      playerCategories,
      matchFormat,
      playersPerMatch,
      callMatchmakingFn,
    );
  }

  if (!rawCourt) {
    return { type: null, teams: null, gameCreation: null, rawCourt: null, selectedScheduledGame: null };
  }

  return {
    type: 'matchmaking',
    teams: { team1: rawCourt.team1, team2: rawCourt.team2 },
    gameCreation: {
      origin: GAME_CREATION_ORIGIN.MATCHMAKING,
      algorithm: MATCHMAKING_ALGORITHM_NAME,
      scheduledGameId: null,
      isAltered: false,
    },
    rawCourt,
    selectedScheduledGame: null,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Run the matchmaking engine with the three-tier fallback strategy.
 * Tier 1: eligibleIds + current gameTypeRestriction
 * Tier 2: fullPoolIds + current gameTypeRestriction  (when tier 1 yields nothing)
 */
function _runWithFallbacks(
  eligibleIds,
  fullPoolIds,
  usingFallbackPool,
  sessionState,
  playersById,
  partnerships,
  playerWaitMetrics,
  gameTypeRestriction,
  playerCategories,
  matchFormat,
  playersPerMatch,
  callMatchmakingFn,
) {
  const call = (ids, restriction, mmOptions = {}) => {
    if (ids.length < playersPerMatch) return null;
    const result = callMatchmakingFn(
      ids,
      1,
      sessionState,
      playersById,
      partnerships,
      [],
      restriction,
      playerCategories,
      mmOptions,
    );
    return result?.courts?.[0] ?? null;
  };

  // Pool expansion gate inside twoStageEngine: narrow eligibleIds first, then full
  // waitlist (with cooldown expanded) before consecutive interaction relaxation.
  const primary = call(eligibleIds, gameTypeRestriction, {
    fullWaitlistPlayerIds: fullPoolIds,
  });
  if (primary) return primary;

  // Belt-and-suspenders: if the narrow tier failed, retry with the full pool directly.
  if (
    !usingFallbackPool
    && fullPoolIds.length >= playersPerMatch
    && fullPoolIds.length > eligibleIds.length
  ) {
    return call(fullPoolIds, gameTypeRestriction, {
      fullWaitlistPlayerIds: fullPoolIds,
    });
  }

  return null;
}
